/**
 * Instead of sprinkling random analytics tracking functions all over the codebase, we watch existing redux actions and fire
 * the appropriate events for them.  This keeps analytics decoupled from application code and allows us to add more analytic
 * data without changing application code.
 */

import {
    handleChatClick,
    handleChatClosed,
    handleChatImpression,
    handleChatMessage,
} from '../actions/shiftTagging';
import {
    BOOTSTRAP_FINISHED,
    BOT_CONVO_STARTED,
    CTA_CLICKED,
    END_CONVO,
    NEW_BOT_MESSAGE,
    REMOTE_VIDEO_ACTIVE,
    SEND_MESSAGE,
    SET_SURVEY_SCORE,
    SMS_CTA_CLICKED,
    SOCKET_EVENT_AGENT_ACCEPTED,
    SOCKET_EVENT_CONTACT_INFO_GATHERED,
    SOCKET_EVENT_CONVERSATION_ENDED,
    SOCKET_EVENT_NEW_BOT_MESSAGE,
    SOCKET_EVENT_NEW_MESSAGE,
    SOCKET_EVENT_SENT_TO_CRM,
    SOCKET_EVENT_STARTED_REAL_CONVO,
    TOGGLE_MINIMIZED,
} from '../typedefs/actionTypes';
import { getConvo, getTeams } from '../selectors/conversation';
import {
    BotConvoStartedAction,
    StartedRealConvoSocketAction,
    ContactInfoGatheredAction,
    CtaClickedAction,
    RealConvoStartedAction,
    SocketEventNewMessageAction,
    SetSurveyScoreAction,
    RemoteVideoActiveAction,
    EndConvoAction,
    ToggleMinimizedAction,
    SendMessageAction,
    SentToCrmSocketAction,
} from '../typedefs/actions';
import { VehicleAttachment, CalculatorAttachment, Attachment, Team } from '../typedefs/store';
import { TranscriptMessage } from '../typedefs/api';
import { DICWindow } from '../typedefs/window';
const flatMap = require('lodash.flatmap');

declare var window: DICWindow;

const ROXANNE_SCORES = {
    startedBotConversation: 5,
    startedRealConversation: 5,
    vehicleSent: 9,
    paymentSent: 9,
    personalizedConversation: 7,
    surveyCompleted: 8,
    videoEnabled: 8,
};

interface FireRoxanneEventFunc {
    (
        label: string,
        options: {
            label?: string;
            name?: string;
            score?: number;
            providerData?: Object;
        },
    ): void;
}

let fireRoxanneEvent: FireRoxanneEventFunc = (
    label,
    { name = 'chat', score = 0, providerData = {} } = {},
) => {
    let customData = { provider: { name: 'Conversations', ...providerData } };

    const payload = {
        detail: {
            label,
            name,
            default_score: score,
            custom_data: customData,
        },
    };

    document.dispatchEvent(new CustomEvent('di_ev', payload));
};

interface FireGAEventFunc {
    (
        options: {
            category?: string;
            action?: string;
            label?: string;
            value?: string | number;
        },
    ): void;
}

let fireGAEvent: FireGAEventFunc = ({
    category = 'Conversations',
    action = 'Click',
    label,
    value,
}) => {
    try {
        if (window.ga && window.ga.getAll && window.ga.getAll().length > 0) {
            window.ga.getAll().forEach(tracker => {
                tracker.send({
                    hitType: 'event',
                    eventLabel: label,
                    eventCategory: category,
                    eventAction: action,
                    eventValue: value,
                });
            });
        }
    } catch (e) {
        // ignore
    }
};

const getUnreadAttachments = (messages: TranscriptMessage[]) => {
    const messagesWithUnreadAttachments = messages
        .filter(m => m.attachments && m.attachments.length > 0) // with attachment
        .filter(m => m.agentId !== null) // from agent
        .filter(m => m.readReceipts.filter(rr => rr.visitorId !== null).length === 0); // unread

    return flatMap(
        messagesWithUnreadAttachments,
        (m: TranscriptMessage) => (m.attachments ? m.attachments : []),
    );
};

const getAttachmentsFromSocketMessage = (json?: string): Attachment[] => {
    if (!json) return [];
    try {
        return JSON.parse(json);
    } catch (e) {
        return [];
    }
};

const getAttachmentsByType = (attachments: Attachment[], type: string) =>
    attachments.filter(a => a.doctype === type);

const fireVehicleEvents = (attachments: Attachment[]) => {
    const vehicles = getAttachmentsByType(attachments, 'vehicle');
    vehicles.forEach((vehicle: VehicleAttachment) => {
        fireRoxanneEvent('Vehicle Sent', {
            score: ROXANNE_SCORES.vehicleSent,
            providerData: { vehicle: { ...vehicle } },
        });
    });
};

const fireCalculatorEvents = (attachments: Attachment[]) => {
    const calculators = getAttachmentsByType(attachments, 'calculator');
    calculators.forEach((calculator: CalculatorAttachment) => {
        fireRoxanneEvent('Payment Sent', {
            score: ROXANNE_SCORES.paymentSent,
            providerData: { payment: { ...calculator } },
        });
    });
};

const fireAttachmentEvents = (attachments: Attachment[]) => {
    fireVehicleEvents(attachments);
    fireCalculatorEvents(attachments);
};

const getNormalizedTeamName = (team?: Team): string => {
    if (!team) return 'Other';

    if (team.name.toLowerCase().includes('sales')) {
        return 'Sales';
    } else if (team.name.toLowerCase().includes('service')) {
        return 'Service';
    } else if (team.name.toLowerCase().includes('parts')) {
        return 'Parts';
    } else {
        return team.name;
    }
};

export default function createMiddleware() {
    return (store: any) => (next: any) => (action: any) => {
        switch (action.type) {
            case BOOTSTRAP_FINISHED: {
                handleChatImpression();
                break;
            }

            case CTA_CLICKED: {
                fireRoxanneEvent('Conversations Icon', { name: 'click' });
                fireGAEvent({ label: 'Conversations Icon' });
                fireGAEvent({ label: 'CTA Clicked' });
                handleChatClick('chat');
                break;
            }

            case SMS_CTA_CLICKED: {
                fireRoxanneEvent('SMS Conversation', {});
                fireGAEvent({ label: 'Text Us Button' });
                handleChatClick('text');
                break;
            }

            case BOT_CONVO_STARTED: {
                fireRoxanneEvent('Bot Conversation Started', {
                    score: ROXANNE_SCORES.startedBotConversation,
                    providerData: { channel: (action as BotConvoStartedAction).payload.channel },
                });
                fireGAEvent({ label: 'Start Convo Button' });
                break;
            }

            case SOCKET_EVENT_CONTACT_INFO_GATHERED: {
                fireRoxanneEvent('Personalized Conversation', {
                    score: ROXANNE_SCORES.personalizedConversation,
                    providerData: {
                        firstName: (action as ContactInfoGatheredAction).payload.firstName,
                        lastName: (action as ContactInfoGatheredAction).payload.lastName,
                        email: (action as ContactInfoGatheredAction).payload.email,
                        phone: (action as ContactInfoGatheredAction).payload.phone,
                    },
                });
                break;
            }

            case SOCKET_EVENT_STARTED_REAL_CONVO: {
                const team = getTeams(store.getState()).find(
                    team => team.id === (action as StartedRealConvoSocketAction).payload.teamId,
                );
                const teamName = getNormalizedTeamName(team);

                fireRoxanneEvent('Started Conversation', {
                    score: ROXANNE_SCORES.startedRealConversation,
                    providerData: {
                        location: 'floating',
                        engagement: 'active',
                        channel: (action as StartedRealConvoSocketAction).payload.channel,
                    },
                });

                fireGAEvent({ action: 'Escalated To Human', label: teamName });
                break;
            }

            case SOCKET_EVENT_AGENT_ACCEPTED: {
                const convo = getConvo(store.getState());
                fireRoxanneEvent('Conversation Accepted', {
                    providerData: { channel: convo.channel },
                });
                break;
            }

            case SEND_MESSAGE: {
                const convo = getConvo(store.getState());
                fireGAEvent({ category: 'CPE-Chat-Engage', label: 'Send Message' });
                fireGAEvent({ action: 'Engage', label: 'Message Sent' });
                handleChatMessage(convo.channel as string, action.payload.text, true);
                break;
            }

            case SOCKET_EVENT_NEW_MESSAGE: {
                const convo = getConvo(store.getState());
                fireAttachmentEvents(getAttachmentsFromSocketMessage(action.payload.attachments));
                handleChatMessage(convo.channel as string, action.payload.messageText, false);
                break;
            }

            case NEW_BOT_MESSAGE: {
                const convo = getConvo(store.getState());
                handleChatMessage(convo.channel as string, action.payload.text, false);
                break;
            }

            case SOCKET_EVENT_NEW_BOT_MESSAGE: {
                const convo = getConvo(store.getState());
                handleChatMessage(convo.channel as string, action.payload.text, false);
                break;
            }

            case SET_SURVEY_SCORE: {
                fireRoxanneEvent('Conversation Survey Completed', {
                    score: ROXANNE_SCORES.surveyCompleted,
                    providerData: { surveyScore: (action as SetSurveyScoreAction).payload.score },
                });
                break;
            }

            case REMOTE_VIDEO_ACTIVE: {
                if ((action as RemoteVideoActiveAction).payload.value === true) {
                    fireRoxanneEvent('Conversation Video/Voice Enabled', {
                        score: ROXANNE_SCORES.videoEnabled,
                    });
                }
                break;
            }

            case END_CONVO: {
                const convo = getConvo(store.getState());
                fireGAEvent({ label: 'Confirm End Conversation' });
                handleChatClosed(convo.channel as string);
                break;
            }

            case SOCKET_EVENT_CONVERSATION_ENDED: {
                const convo = getConvo(store.getState());
                handleChatClosed(convo.channel as string);
                break;
            }

            case TOGGLE_MINIMIZED: {
                const convo = getConvo(store.getState());
                if (convo.minimized === false) fireGAEvent({ label: 'Minimize Conversations' });
                break;
            }

            case SOCKET_EVENT_SENT_TO_CRM: {
                if ((action as SentToCrmSocketAction).payload.type === 'message') {
                    fireGAEvent({ action: 'Submitted', label: 'Leave a Message' });
                    fireGAEvent({ action: 'Form', label: 'Leave a Message' });
                }
                break;
            }

            default:
                break;
        }

        return next(action);
    };
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/store/AnalyticsEventMiddleware.ts